import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({})

// 🧪 TESTING BYPASS FLAG - Set to true to bypass real authentication for testing
const BYPASS_AUTH_FOR_TESTING = import.meta.env.VITE_BYPASS_AUTH === 'true';
const BUILD_ID = '2026-03-08-header-dropdown-v2'; // Unique ID to confirm build version in user console

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profile, setProfile] = useState(null)
  const [initialized, setInitialized] = useState(false)
  const [originalUser, setOriginalUser] = useState(null)

  // Track currently fetching profile to prevent race conditions
  const fetchingProfileFor = React.useRef(null);
  const initializationTimeoutCalled = React.useRef(false);

  // 🧹 Auto-clear mock and Supabase data if requested or bypass is disabled
  useEffect(() => {
    console.log(`🚀 [DIAGNOSTIC] App Build ID: ${BUILD_ID}`);
    if (!BYPASS_AUTH_FOR_TESTING) {
      const mockKeys = ['user', 'profile'];
      mockKeys.forEach(key => {
        if (localStorage.getItem(key)) {
          console.log(`🧹 Clearing mock key: ${key}`);
          localStorage.removeItem(key);
        }
      });
      // NOTE: Removed destructive Supabase auth token clearing
      // that was wiping the session on every page load
    }
  }, []);

  // ⚠️ PROTECTED FUNCTION - DO NOT MODIFY OR ADD ASYNC OPERATIONS
  // This is a Supabase auth state change listener that must remain synchronous
  const handleAuthStateChange = (event, session) => {
    console.log(`🔐 [DIAGNOSTIC] Auth state change event: ${event}`, { hasUser: !!session?.user });

    // SYNC OPERATIONS ONLY - NO ASYNC/AWAIT ALLOWED
    if (session?.user) {
      setUser(session?.user)
    } else {
      setUser(null)
      setProfile(null)
      fetchingProfileFor.current = null;
    }

    // Mark as initialized once we get our first event
    if (!initialized) {
      console.log('🚀 [DIAGNOSTIC] First auth event received, marking as initialized.');
      setInitialized(true);
    }
  }

  const fetchProfileWithLock = async (userId) => {
    if (!userId) return null;
    if (fetchingProfileFor.current === userId) {
      console.log(`⏳ [DIAGNOSTIC] Profile fetch already in progress for ${userId}, skipping.`);
      return null;
    }

    fetchingProfileFor.current = userId;
    try {
      console.log(`📡 [DIAGNOSTIC] Fetching profile for user: ${userId}`);
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

      if (error) {
        if (error?.code !== 'PGRST116') {
          console.error('❌ [DIAGNOSTIC] Error fetching profile:', error);
        } else {
          console.log('ℹ️ [DIAGNOSTIC] No profile found (new user?)');
        }
        return null;
      }

      console.log('✅ [DIAGNOSTIC] Profile fetched successfully');
      return data;
    } catch (err) {
      console.error('❌ [DIAGNOSTIC] Profile fetch exception:', err);
      return null;
    } finally {
      if (fetchingProfileFor.current === userId) {
        fetchingProfileFor.current = null;
      }
    }
  }

  useEffect(() => {
    let mounted = true;
    let timeoutId = null;

    // 🧪 TESTING BYPASS - Skip auth initialization if testing mode is enabled
    if (BYPASS_AUTH_FOR_TESTING) {
      console.log('🧪 TESTING MODE: Bypassing authentication - all pages accessible');

      // Create a mock authenticated user for testing
      const mockUser = {
        id: '00000000-0000-0000-0000-000000000000',
        email: 'test@example.com',
        user_metadata: {
          full_name: 'Test User'
        }
      };

      if (mounted) {
        setUser(mockUser);
        setProfile({
          id: '00000000-0000-0000-0000-000000000000',
          full_name: 'Test User',
          email: 'test@example.com',
          role: 'admin',
          subscription_tier: 'premium',
          subscription_status: 'active',
          trial_end_date: null,
          created_at: new Date()?.toISOString()
        });
        setInitialized(true);
        setTimeout(() => {
          if (mounted) {
            setLoading(false);
            console.log('🧪 Testing mode initialization complete');
          }
        }, 100);
      }

      return () => {
        mounted = false;
        if (timeoutId) clearTimeout(timeoutId);
      };
    }

    // Get initial session with improved error handling and timing
    const initializeAuth = async () => {
      try {
        console.log('🚀 [DIAGNOSTIC] initializeAuth started');

        if (!supabase) {
          console.error('❌ [DIAGNOSTIC] Supabase not initialized.');
          return;
        }

        // We check getSession() but we'll accept AbortError because 
        // onAuthStateChange will also fire for the initial load.
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          // If it's an AbortError, don't be fatal, let the listener handle it
          if (error.message?.includes('signal is aborted')) {
            console.warn('⚠️ [DIAGNOSTIC] getSession() aborted - relying on onAuthStateChange');
          } else {
            console.error('❌ [DIAGNOSTIC] Error getting initial session:', error);
          }
          if (mounted) {
            setInitialized(true);
            setLoading(false);
          }
          return;
        }

        if (session?.user && mounted) {
          console.log('✅ [DIAGNOSTIC] Initial session found for user:', session.user.id);
          setUser(session.user);
          const userProfile = await fetchProfileWithLock(session.user.id);
          if (mounted && userProfile) {
            setProfile(userProfile);
          }
        }

        if (mounted) {
          setInitialized(true);
          setLoading(false);
        }
      } catch (initError) {
        console.log('ℹ️ [DIAGNOSTIC] Auth init catch (likely swallowed AbortError)');
        if (mounted) {
          setInitialized(true);
          setLoading(false);
        }
      }
    };

    // Initialize auth with a slight delay
    setTimeout(() => {
      if (mounted) initializeAuth();
    }, 500);

    // Safety timeout to prevent infinite loading
    const safetyTimer = setTimeout(() => {
      if (mounted && (loading || !initialized)) {
        console.warn('⚠️ [DIAGNOSTIC] Auth initialization safety timeout triggered');
        setLoading(false);
        setInitialized(true);
        initializationTimeoutCalled.current = true;
      }
    }, 6000);

    // Set up auth state listener
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      console.log(`🔐 [DIAGNOSTIC] onAuthStateChange event: ${event}`);

      // Handle the auth state change
      handleAuthStateChange(event, session);

      // Fetch profile after auth state change
      if (session?.user && mounted) {
        const userProfile = await fetchProfileWithLock(session.user.id);
        if (mounted && userProfile) {
          setProfile(userProfile);
        }
      }
    });

    // Cleanup function
    return () => {
      mounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      clearTimeout(safetyTimer);
      subscription?.unsubscribe();
    };
  }, [])

  const signUp = async (email, password, options = {}) => {
    try {
      if (BYPASS_AUTH_FOR_TESTING) {
        console.log('🧪 TESTING MODE: Bypassing real signUp, returning mock user');
        const mockUser = {
          id: '00000000-0000-0000-0000-000000000000',
          email: email || 'test@example.com',
          user_metadata: {
            full_name: options?.fullName || 'Test User'
          }
        };
        setUser(mockUser);
        setProfile({
          id: '00000000-0000-0000-0000-000000000000',
          full_name: options?.fullName || 'Test User',
          email: email || 'test@example.com',
          role: 'admin',
          subscription_tier: 'premium',
          subscription_status: 'active',
          trial_end_date: null,
          created_at: new Date()?.toISOString()
        });
        return { data: { user: mockUser }, error: null };
      }

      setLoading(true);

      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard-overview`,
          data: {
            full_name: options?.fullName || '',
            ...options?.data
          }
        }
      })

      if (error) {
        console.error('❌ Signup error:', error);
        throw error;
      }

      return { data, error: null }
    } catch (error) {
      console.error('❌ Signup exception:', error);
      return { data: null, error }
    } finally {
      setLoading(false);
    }
  }

  const signIn = async (email, password) => {
    try {
      console.log('🚀 [DIAGNOSTIC] Starting signIn process for:', email);

      if (!supabase) {
        console.error('❌ [DIAGNOSTIC] Supabase client is NULL. Check environment variables!');
        throw new Error('Database connection not initialized. Please contact support.');
      }

      console.log('📡 [DIAGNOSTIC] Supabase URL:', supabase?.supabaseUrl || 'unknown');

      if (BYPASS_AUTH_FOR_TESTING) {
        // ... (bypass logic remains unchanged)
        console.log('🧪 TESTING MODE: Bypassing real signIn, returning mock user');
        const mockUser = {
          id: '00000000-0000-0000-0000-000000000000',
          email: email || 'test@example.com',
          user_metadata: {
            full_name: 'Test User'
          }
        };
        setUser(mockUser);
        setProfile({
          id: '00000000-0000-0000-0000-000000000000',
          full_name: 'Test User',
          email: email || 'test@example.com',
          role: 'admin',
          subscription_tier: 'premium',
          subscription_status: 'active',
          trial_end_date: null,
          created_at: new Date()?.toISOString()
        });
        return { data: { user: mockUser }, error: null };
      }

      setLoading(true);
      console.log('📡 [DIAGNOSTIC] Calling supabase.auth.signInWithPassword...');

      const startTime = Date.now();
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      const duration = Date.now() - startTime;

      console.log(`⏱️ [DIAGNOSTIC] signInWithPassword took ${duration}ms`);

      if (error) {
        console.error('❌ [DIAGNOSTIC] Signin error returned from Supabase:', {
          message: error.message,
          status: error.status,
          name: error.name,
          fullError: error
        });
        throw error;
      }

      console.log('✅ [DIAGNOSTIC] Signin successful, user ID:', data?.user?.id);
      return { data, error: null }
    } catch (error) {
      console.error('❌ [DIAGNOSTIC] Signin exception caught:', {
        message: error.message,
        stack: error.stack,
        fullError: error
      });
      return { data: null, error }
    } finally {
      setLoading(false);
    }
  }

  const signInWithGoogle = async () => {
    try {
      if (BYPASS_AUTH_FOR_TESTING) {
        console.log('🧪 TESTING MODE: Bypassing real signInWithGoogle, returning mock user');
        const mockUser = {
          id: '00000000-0000-0000-0000-000000000000',
          email: 'google-test@example.com',
          user_metadata: {
            full_name: 'Google Test User'
          }
        };
        setUser(mockUser);
        setProfile({
          id: '00000000-0000-0000-0000-000000000000',
          full_name: 'Google Test User',
          email: 'google-test@example.com',
          role: 'admin',
          subscription_tier: 'premium',
          subscription_status: 'active',
          trial_end_date: null,
          created_at: new Date()?.toISOString()
        });

        // Navigate manually since OAuth redirect won't happen
        setTimeout(() => {
          window.location.href = '/dashboard-overview';
        }, 100);

        return { data: { user: mockUser }, error: null };
      }

      const { data, error } = await supabase?.auth?.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location?.origin + '/dashboard-overview'
        }
      })

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error }
    }
  }

  const signInWithApple = async () => {
    try {
      if (BYPASS_AUTH_FOR_TESTING) {
        console.log('🧪 TESTING MODE: Bypassing real signInWithApple, returning mock user');
        const mockUser = {
          id: '00000000-0000-0000-0000-000000000000',
          email: 'apple-test@example.com',
          user_metadata: {
            full_name: 'Apple Test User'
          }
        };
        setUser(mockUser);
        setProfile({
          id: '00000000-0000-0000-0000-000000000000',
          full_name: 'Apple Test User',
          email: 'apple-test@example.com',
          role: 'admin',
          subscription_tier: 'premium',
          subscription_status: 'active',
          trial_end_date: null,
          created_at: new Date()?.toISOString()
        });

        // Navigate manually
        setTimeout(() => {
          window.location.href = '/dashboard-overview';
        }, 100);

        return { data: { user: mockUser }, error: null };
      }

      const { data, error } = await supabase?.auth?.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: window.location?.origin + '/dashboard-overview'
        }
      })

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error }
    }
  }

  const signOut = async () => {
    try {
      if (BYPASS_AUTH_FOR_TESTING || !supabase) {
        console.log('🧪 TESTING MODE: Bypassing real signOut');
        setUser(null);
        setProfile(null);
        return { error: null };
      }

      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      setUser(null);
      setProfile(null);
      return { error: null };
    } catch (error) {
      console.error('❌ Signout error:', error);
      // Force clear local state even if supabase signout fails
      setUser(null);
      setProfile(null);
      return { error };
    }
  }

  const resetPassword = async (email) => {
    try {
      const { error } = await supabase?.auth?.resetPasswordForEmail(email)
      if (error) throw error
      return { error: null }
    } catch (error) {
      return { error }
    }
  }

  const updateProfile = async (updates) => {
    try {
      if (!user?.id) throw new Error('No authenticated user')

      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()

      if (error) throw error

      setProfile(data)
      return { data, error: null }
    } catch (error) {
      return { data: null, error }
    }
  }

  const impersonateUser = async (userId) => {
    try {
      if (profile?.role !== 'admin') throw new Error('Not authorized');
      const targetProfile = await fetchProfileWithLock(userId);
      if (targetProfile) {
        setOriginalUser({ user, profile });
        setUser({ id: userId, email: targetProfile.email, role: targetProfile.role });
        setProfile(targetProfile);
        return { error: null };
      }
      return { error: new Error('User profile not found') };
    } catch (error) {
      console.error('❌ Impersonation error:', error);
      return { error };
    }
  };

  const stopImpersonating = () => {
    if (originalUser) {
      setUser(originalUser.user);
      setProfile(originalUser.profile);
      setOriginalUser(null);
    }
  };

  const effectiveProfile = profile ? {
    ...profile,
    subscription_tier: profile.role === 'admin' ? 'premium' : profile.subscription_tier
  } : null;

  const value = {
    user,
    profile: effectiveProfile,
    role: profile?.role || 'user',
    isAdmin: (() => {
      const MASTER_ADMIN_EMAILS = ['benjamin.hamilton.jones@gmail.com', 'ben.hamilton.jones@hotmail.com', 'admin@pocketchapters.com', 'halcycon907@hotmail.co.uk'];
      // Check role, original user role, or master email as safety fallback
      const adminStatus = 
        profile?.role === 'admin' || 
        originalUser?.profile?.role === 'admin' ||
        (user?.email && MASTER_ADMIN_EMAILS.includes(user.email.toLowerCase()));
      
      console.log(`🛡️ [DIAGNOSTIC] Auth Check:`, {
        isAdmin: adminStatus,
        role: profile?.role,
        email: user?.email,
        isImpersonating: !!originalUser,
        originalRole: originalUser?.profile?.role
      });
      return adminStatus;
    })(),
    loading,
    initialized,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithApple,
    signOut,
    resetPassword,
    updateProfile,
    impersonateUser,
    stopImpersonating,
    isImpersonating: !!originalUser,
    isAuthenticated: BYPASS_AUTH_FOR_TESTING ? true : !!user // Always authenticated in testing mode
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider